import type { UniverseConfig, UniverseProfile } from "@/lib/universe";
import type { UniverseCondition } from "../types";
import { clamp } from "./helpers";

export function buildUniverseDescription(
  universe: { name: string; code: string; config: UniverseConfig; profile: UniverseProfile },
  condition: UniverseCondition,
  sharedUniverse: boolean
): string {
  const { name, code, config, profile } = universe;
  const sharedPrefix = sharedUniverse
    ? `In this timeline, this self exists within the shared cosmos of ${name} (${code}) chosen for all four branches.`
    : `This self inhabits the imagined cosmos of ${name} (${code}).`;

  let conditionSentence = "";
  if (condition === "hospitable") {
    conditionSentence = `With an estimated habitability of ${profile.habitability}/100 (${profile.habitabilityLabel}), standard terrestrial planetary conditions flourish under balanced cosmological constants.`;
  } else if (condition === "marginal") {
    conditionSentence = `Holding a marginal habitability rating of ${profile.habitability}/100 (${profile.habitabilityLabel}), existence requires sheltered subterranean biomes, orbital habitats, or adaptation to exotic non-standard chemistry.`;
  } else {
    conditionSentence = `Framed strictly as an imagined thought experiment (${profile.habitability}/100, ${profile.habitabilityLabel}), fundamental physical barriers preclude carbon biology; existence here is conceived purely as self-organizing informational or field-based resonance.`;
  }

  const physicsDetail = `The continuum manifests in ${config.dimensions} spatial dimensions with ${config.topology} topology, where gravity scales at ${config.gravity.toFixed(2)}× and cosmological expansion drives toward ${profile.fate}.`;

  const stageSentence = `Observed at an evolutionary epoch of ${config.ageGyr.toFixed(1)} Gyr, the cosmos resides in its ${profile.stage}, characterized by ${profile.visual.structure} structures and ${profile.visual.expansion} geometry.`;

  return `${sharedPrefix} ${conditionSentence} ${physicsDetail} ${stageSentence}`;
}

export function buildWorldDetails(
  archetype: string,
  universe: { name: string; code: string; config: UniverseConfig; profile: UniverseProfile },
  condition: UniverseCondition,
  interestPick: string,
  extraDetails: { label: string; value: string }[]
): { label: string; value: string }[] {
  const details: { label: string; value: string }[] = [];

  // 1. Cosmic Habitat / Environment
  if (condition === "hostile") {
    details.push({
      label: "Speculative Substrate",
      value: "Hypothetical non-biological informational pattern within radiation flows; life as we know it could not exist here.",
    });
  } else if (condition === "marginal") {
    details.push({
      label: "Planetary Habitat",
      value: "Atmospheric shielding domes and deep mantle geothermal hubs compensating for extreme radiation.",
    });
  } else {
    details.push({
      label: "Primary Habitat",
      value: "Terrestrial biospheres sustained by stable stellar illumination and balanced atmospheric pressure.",
    });
  }

  // 2. Cosmic Physics Factor
  details.push({
    label: "Physical Constants",
    value: `${universe.config.dimensions}D space, ${universe.config.gravity.toFixed(2)}× gravity, ${universe.config.topology} topology`,
  });

  // 3. Cultural / Interest artifact
  details.push({
    label: "Local Paradigm",
    value: interestPick,
  });

  // 4..5 Extra archetypal details
  for (const extra of extraDetails) {
    if (details.length < 6) {
      details.push(extra);
    }
  }

  return details;
}

export interface TraitBankItem {
  name: string;
  description: string;
}

/**
 * Expanded trait bank: at least 6 distinct trait variants per strength.
 */
export const STRENGTH_TRAIT_MAP: Record<string, TraitBankItem[]> = {
  curiosity: [
    { name: "Insatiable Inquisitiveness", description: "Relentlessly deconstructs unexamined assumptions and follows anomalous patterns." },
    { name: "Empirical Drive", description: "Demands direct observation and repeatable evidence before granting assent." },
    { name: "Hypothesis Testing", description: "Transforms everyday dilemmas into structured trials to discover what holds true." },
    { name: "First-Principles Hunger", description: "Strips away derivative opinions to trace problems back to fundamental axioms." },
    { name: "Anomalous Perception", description: "Notices hairline contradictions in records that others gloss over." },
    { name: "Wide-Spectrum Inquiry", description: "Crosses domain boundaries without hesitation, gathering clues from far-flung disciplines." },
  ],
  empathy: [
    { name: "Deep Resonance", description: "Instinctively maps emotional undercurrents and silent motives across disparate minds." },
    { name: "Compassionate Attunement", description: "Prioritizes communal healing and psychological cohesion above procedural expedience." },
    { name: "Grief Accompaniment", description: "Walks alongside individuals in crisis without offering shallow platitudes." },
    { name: "Bridge-Building Ear", description: "Listens beneath ideological posturing to address the raw vulnerability powering it." },
    { name: "Unspoken Discernment", description: "Detects the exact moment a colleague or companion has reached their breaking point." },
    { name: "Generous Reciprocity", description: "Shares credit, hospitality, and emotional safety freely across divided circles." },
  ],
  discipline: [
    { name: "Unshakable Focus", description: "Executes intricate long-range commitments with quiet, deliberate consistency." },
    { name: "Rigorous Precision", description: "Eliminates margin of error through meticulous practice and measured restraint." },
    { name: "Punctual Economy", description: "Guards daily working hours ruthlessly against trivial distraction and performative noise." },
    { name: "Ascetic Stamina", description: "Maintains composure and high-caliber craft under prolonged physical or logistical deprivation." },
    { name: "Systemic Cadence", description: "Installs recurring procedural checkpoints that prevent compound organizational failure." },
    { name: "Tempered Habituation", description: "Builds formidable mastery through unremarkable daily repetition over years." },
  ],
  creativity: [
    { name: "Lateral Synthesis", description: "Bridges unassociated domains into elegant, unconventional paradigms." },
    { name: "Expressive Inventiveness", description: "Manifests novel conceptual structures that reshape ordinary perception." },
    { name: "Metaphorical Insight", description: "Explains convoluted paradoxes through startling, crystalline imagery." },
    { name: "Iterative Ingenuity", description: "Turns unexpected experimental errors into productive breakthroughs." },
    { name: "Divergent Craft", description: "Refuses standardized templates, handcrafting custom solutions tailored to unique constraints." },
    { name: "Subversive Playfulness", description: "Disarms rigid dogmas by testing playful variations that expose unspoken flaws." },
  ],
  courage: [
    { name: "Tempered Fortitude", description: "Steps into ideological or physical friction without hesitation when principle demands." },
    { name: "Frontier Will", description: "Willingly embraces uncertain outcomes to breach established boundaries." },
    { name: "Solitary Integrity", description: "Voices unpopular truths calmly in rooms dominated by unanimous consensus." },
    { name: "Crisis Decisiveness", description: "Commands clarity and moves decisively when catastrophic accidents unravel the plan." },
    { name: "Sacrificial Bastion", description: "Absorbs institutional or physical hazards to shield more vulnerable companions." },
    { name: "Unflinching Accountability", description: "Owns personal missteps publicly without deflection or excuses." },
  ],
  logic: [
    { name: "Analytical Clarity", description: "Dissects convoluted systemic entanglements into pristine axiomatic premises." },
    { name: "Structural Discernment", description: "Detects fallacies and systemic vulnerabilities with dispassionate speed." },
    { name: "Algorithmic Symmetry", description: "Designs procedural checks that guarantee consistency across millions of interactions." },
    { name: "Causal Mapping", description: "Traces hidden second- and third-order ripple effects before taking irreversible action." },
    { name: "Dispassionate Triage", description: "Assesses catastrophic problems on real metrics rather than anxious superstition." },
    { name: "Axiomatic Discipline", description: "Refuses to build on unproven premises regardless of institutional momentum." },
  ],
  humor: [
    { name: "Subversive Irony", description: "Punctures pomposity and relieves existential tension with pinpoint wit." },
    { name: "Lucid Levity", description: "Keeps catastrophic weight in perspective through playful, grounding realism." },
    { name: "Disarming Warmth", description: "Defuses hostile standoffs by inviting opponents to laugh at shared absurdity." },
    { name: "Mischievous Perspective", description: "Reminds overly serious authorities that every empire eventually turns into pottery shards." },
    { name: "Tragicomedy Mastery", description: "Finds the bittersweet, redemptive spark embedded in difficult losses." },
    { name: "Playful Defiance", description: "Celebrates daily small pleasures in defiance of oppressive gloom." },
  ],
  patience: [
    { name: "Enduring Vigilance", description: "Allows complex systems to ripen across deep generational or seasonal spans." },
    { name: "Gentle Persistence", description: "Dissolves stubborn obstacles through continuous, unhurried pressure." },
    { name: "Deep-Time Stewardship", description: "Plants orchards and drafts treatises meant to mature decades after death." },
    { name: "Unreactive Poise", description: "Allows provoked storms to expend their fury before offering a measured response." },
    { name: "Attuned Waiting", description: "Discerns the quiet right moment for action rather than forcing premature closure." },
    { name: "Cyclical Tolerance", description: "Endures fallow seasons calmly, trusting the natural return of fruitful periods." },
  ],
};
